'use client';

import { CalendarDateRangeIcon } from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

type CalendarProps = {
  selectedDate: Date | null;
  onChange: (date: Date | null) => void;
  minDateString?: string;
  maxDateString?: string;
  showTimeSelect?: boolean;
  showYearDropdown?: boolean;
  showMonthDropdown?: boolean;
  scrollableYearDropdown?: boolean;
  dateFormat?: string;
  dayMonthOnly?: boolean;
};

const Calendar = ({
  selectedDate,
  onChange,
  minDateString,
  maxDateString,
  showTimeSelect = false,
  showYearDropdown = false,
  showMonthDropdown = true,
  scrollableYearDropdown = false,
  dateFormat = 'MMMM d, yyyy',
  dayMonthOnly = false,
}: CalendarProps) => {
  const handleDateChange = (date: Date | null) => {
    if (dayMonthOnly && date) {
      const adjustedDate = new Date(date);
      adjustedDate.setFullYear(2000);
      onChange(adjustedDate);
    } else {
      onChange(date);
    }
  };

  const minTime = new Date();
  minTime.setHours(8, 0, 0, 0);

  const maxTime = new Date();
  maxTime.setHours(18, 0, 0, 0);

  const getAdjustedDate = (dateString: string | undefined) => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    if (dayMonthOnly) date.setFullYear(2000);
    return date;
  };

  const minDate = (dateString: string | undefined) => {
    if (!dateString) return new Date();
    const date = new Date(dateString);
    if (dayMonthOnly) date.setFullYear(2000);
    return date;
  };

  const finalDateFormat = dayMonthOnly ? 'MMMM d' : dateFormat;
  const finalShowYearDropdown = dayMonthOnly ? false : showYearDropdown;

  return (
    <div className="w-full relative">
      <DatePicker
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat={finalDateFormat}
        placeholderText="Select your date"
        showTimeSelect={showTimeSelect}
        timeFormat={showTimeSelect ? 'HH:mm' : undefined}
        timeIntervals={showTimeSelect ? 15 : undefined}
        timeCaption={showTimeSelect ? 'Time' : undefined}
        minTime={showTimeSelect ? minTime : undefined}
        maxTime={showTimeSelect ? maxTime : undefined}
        minDate={minDate(minDateString)}
        maxDate={getAdjustedDate(maxDateString)}
        showYearDropdown={finalShowYearDropdown}
        showMonthDropdown={showMonthDropdown}
        scrollableYearDropdown={scrollableYearDropdown}
        yearDropdownItemNumber={scrollableYearDropdown ? 60 : undefined}
        wrapperClassName="w-full"
        calendarClassName={`${dayMonthOnly ? undefined : 'w-[21.5rem] sm:w-full flex flex-row items-start px-2'}`}
        popperClassName="w-full"
        popperPlacement="bottom-start"
        className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm focus:border-crunchies-red focus:ring-crunchies-red"
        renderCustomHeader={
          dayMonthOnly
            ? ({ monthDate, decreaseMonth, increaseMonth }) => (
                <div className="flex items-center justify-center gap-2 m-2">
                  <button
                    type="button"
                    onClick={decreaseMonth}
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    {'<'}
                  </button>
                  <span className="font-medium">
                    {monthDate.toLocaleString('default', { month: 'long' })}
                  </span>
                  <button
                    type="button"
                    onClick={increaseMonth}
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    {'>'}
                  </button>
                </div>
              )
            : undefined
        }
      />
      <CalendarDateRangeIcon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
  );
};

export default Calendar;
